import React, { useState, useContext } from 'react';
import { joinQueue, leaveQueue } from '../../api/quickDuel';
import { UserContext } from '../../context/UserContext';
import { useQuickDuel } from '../../context/QuickDuelContext';
import HrnbCard from '../controls/HrnbCard';
import HrnbButton from '../controls/HrnbButton';
import HrnbSelect from '../controls/HrnbSelect';
import HrnbAlert from '../controls/HrnbAlert';
import HrnbLoader from '../controls/HrnbLoader';
import HrnbSwitch from '../controls/HrnbSwitch';
import '../../css/duels/QuickDuel.css';

/**
 * Компонент быстрого поиска дуэли (Quick Match)
 * Использует QuickDuelContext для управления очередью и WebSocket
 */
function QuickDuel() {
    const { currentUser } = useContext(UserContext);
    const {
        isInQueue,
        queueStatus,
        queueStats,
        isLoading: contextLoading,
        error: contextError,
        format,
        estimatedWaitSeconds,
        positionInQueue,
        joinQueue: contextJoinQueue,
        leaveQueue: contextLeaveQueue,
    } = useQuickDuel();

    // Локальное состояние формы
    const [formData, setFormData] = useState({
        preferredFormat: 'SOLO',
        preferredRoundType: 'SINGLE_ROUND',
        roundContentType: 'AUDIO',
        minRating: '',
        maxRating: '',
        allowHigherRating: true
    });

    const [localLoading, setLocalLoading] = useState(false);
    const [localError, setLocalError] = useState(null);
    const [localSuccess, setLocalSuccess] = useState(null);

    const isLoading = contextLoading || localLoading;
    const error = contextError || localError;

    // Вход в очередь
    const handleJoinQueue = async () => {
        if (!currentUser) {
            setLocalError('Необходимо войти в систему');
            return;
        }

        setLocalLoading(true);
        setLocalError(null);
        setLocalSuccess(null);

        try {
            const data = {
                preferredFormat: formData.preferredFormat,
                preferredRoundType: formData.preferredRoundType,
                roundContentType: formData.roundContentType,
                minRating: formData.minRating ? parseInt(formData.minRating) : undefined,
                maxRating: formData.maxRating ? parseInt(formData.maxRating) : undefined,
                allowHigherRating: formData.allowHigherRating
            };

            await contextJoinQueue(
                formData.preferredFormat,
                formData.preferredRoundType,
                data
            );

            setLocalSuccess('Поиск соперника начался...');
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Не удалось войти в очередь';

            // Если пользователь уже в очереди
            if (errorMessage.includes('already have an active queue') || errorMessage.includes('уже в очереди')) {
                setLocalSuccess(null);
                setLocalLoading(false);
                return;
            }

            setLocalError(errorMessage);
        } finally {
            setLocalLoading(false);
        }
    };

    // Выход из очереди
    const handleLeaveQueue = async () => {
        setLocalLoading(true);
        setLocalError(null);

        try {
            await contextLeaveQueue();
            setLocalSuccess('Вы вышли из очереди поиска');
        } catch (err) {
            setLocalError('Не удалось выйти из очереди');
        } finally {
            setLocalLoading(false);
        }
    };

    const formatLabels = {
        SOLO: '1 на 1',
        TEAM_2V2: '2 на 2',
        TEAM_3V3: '3 на 3'
    };

    const statusLabels = {
        WAITING: '🔍 Поиск соперника',
        SEARCHING: '🔍 Поиск соперника',
        MATCHED: '✅ Соперник найден',
        ACCEPTED: '✓ Принято',
        DECLINED: '✗ Отклонено',
        ACTIVE: '▶️ Активный матч',
        CANCELLED: '⛔ Отменено'
    };

    if (!currentUser) {
        return (
            <HrnbCard title="⚡ Быстрая дуэль">
                <div className="quick-duel__auth-required">
                    <p>🔐 Войдите, чтобы начать поиск соперника</p>
                    <a href="/login">
                        <HrnbButton variant="primary">Войти</HrnbButton>
                    </a>
                </div>
            </HrnbCard>
        );
    }

    return (
        <HrnbCard
            title={
                <span>
                    ⚡ Быстрая дуэль
                    <span className="quick-duel__ws-status" style={{ marginLeft: '10px', fontSize: '0.85rem' }}>
                        {queueStatus ? '🟢 Онлайн' : '🟡 Оффлайн'}
                    </span>
                </span>
            }
            className="quick-duel"
        >
            {error && (
                <HrnbAlert variant="danger" onClose={() => {
                    setLocalError(null);
                }}>
                    {error}
                </HrnbAlert>
            )}

            {localSuccess && (
                <HrnbAlert variant="success" onClose={() => setLocalSuccess(null)}>
                    {localSuccess}
                </HrnbAlert>
            )}

            {/* Статистика очереди */}
            {queueStats && (
                <div className="quick-duel__stats">
                    <div className="quick-duel__stat">
                        <span className="quick-duel__stat-value">{queueStats.activeQueues || 0}</span>
                        <span className="quick-duel__stat-label">Игроков в очереди</span>
                    </div>
                    <div className="quick-duel__stat">
                        <span className="quick-duel__stat-value">{queueStats.averageWaitSeconds || 0} сек</span>
                        <span className="quick-duel__stat-label">Среднее время поиска</span>
                    </div>
                    <div className="quick-duel__stat">
                        <span className="quick-duel__stat-value">{queueStats.soloQueues || 0}</span>
                        <span className="quick-duel__stat-label">Соло</span>
                    </div>
                </div>
            )}

            {!isInQueue ? (
                <>
                    {/* Форма настроек */}
                    <div className="quick-duel__settings">
                        <HrnbSelect
                            label="Формат *"
                            value={formData.preferredFormat}
                            onChange={(value) => setFormData(prev => ({ ...prev, preferredFormat: value }))}
                            options={[
                                { value: 'SOLO', label: '1 на 1' },
                                { value: 'TEAM_2V2', label: '2 на 2' },
                                { value: 'TEAM_3V3', label: '3 на 3' }
                            ]}
                        />

                        <HrnbSelect
                            label="Предпочтительный формат *"
                            value={formData.preferredFormat}
                            onChange={(value) => setFormData(prev => ({ ...prev, preferredFormat: value }))}
                            options={[
                                { value: 'SOLO', label: '1 на 1' },
                                { value: 'TEAM_2V2', label: '2 на 2' },
                                { value: 'ANY', label: 'Любой' }
                            ]}
                        />

                        <HrnbSelect
                            label="Тип раундов *"
                            value={formData.preferredRoundType}
                            onChange={(value) => setFormData(prev => ({ ...prev, preferredRoundType: value }))}
                            options={[
                                { value: 'SINGLE_ROUND', label: '1 раунд' },
                                { value: 'BEST_OF_THREE', label: 'До 3 побед' }
                            ]}
                        />

                        <HrnbSelect
                            label="Тип контента *"
                            value={formData.roundContentType}
                            onChange={(value) => setFormData(prev => ({ ...prev, roundContentType: value }))}
                            options={[
                                { value: 'AUDIO', label: 'Аудио' },
                                { value: 'TEXT', label: 'Текст' },
                                { value: 'HYBRID', label: 'Гибрид (аудио + текст)' }
                            ]}
                        />

                        <div className="quick-duel__rating-range">
                            <HrnbSelect
                                label="Мин. рейтинг соперника"
                                value={formData.minRating}
                                onChange={(value) => setFormData(prev => ({ ...prev, minRating: value }))}
                                options={[
                                    { value: '', label: 'Любой' },
                                    { value: '800', label: '800+' },
                                    { value: '1000', label: '1000+' },
                                    { value: '1200', label: '1200+' },
                                    { value: '1400', label: '1400+' }
                                ]}
                            />

                            <HrnbSelect
                                label="Макс. рейтинг соперника"
                                value={formData.maxRating}
                                onChange={(value) => setFormData(prev => ({ ...prev, maxRating: value }))}
                                options={[
                                    { value: '', label: 'Любой' },
                                    { value: '1200', label: '1200' },
                                    { value: '1400', label: '1400' },
                                    { value: '1600', label: '1600' },
                                    { value: '1800', label: '1800' }
                                ]}
                            />
                        </div>

                        <HrnbSwitch
                            checked={formData.allowHigherRating}
                            onChange={(checked) => setFormData(prev => ({ ...prev, allowHigherRating: checked }))}
                            label="Разрешить соперников с рейтингом выше"
                        />
                    </div>

                    <HrnbButton
                        variant="primary"
                        onClick={handleJoinQueue}
                        disabled={isLoading}
                        className="quick-duel__start"
                        size="lg"
                    >
                        {isLoading ? 'Поиск...' : '🔍 Начать поиск соперника'}
                    </HrnbButton>
                </>
            ) : (
                <>
                    {/* Статус в очереди */}
                    <div className="quick-duel__status">
                        <div className="quick-duel__status-icon">
                            <HrnbLoader size="lg" />
                        </div>
                        <h3 className="quick-duel__status-title">Поиск соперника...</h3>
                        <p className="quick-duel__status-text">
                            {positionInQueue ? (
                                <>Ваша позиция в очереди: <strong>#{positionInQueue}</strong></>
                            ) : (
                                <>Ожидайте, подбираем подходящего соперника...</>
                            )}
                        </p>

                        {estimatedWaitSeconds !== null && estimatedWaitSeconds !== undefined && (
                            <p className="quick-duel__status-wait">
                                Примерное время ожидания: {estimatedWaitSeconds} сек
                            </p>
                        )}

                        {queueStatus && (
                            <p className="quick-duel__status-format">
                                Статус: {statusLabels[queueStatus] || queueStatus}
                            </p>
                        )}
                    </div>

                    <div className="quick-duel__buttons">
                        <HrnbButton
                            variant="secondary"
                            onClick={handleLeaveQueue}
                            disabled={isLoading}
                            className="quick-duel__leave"
                        >
                            {isLoading ? 'Выход...' : '🚪 Выйти из очереди'}
                        </HrnbButton>
                    </div>
                </>
            )}
        </HrnbCard>
    );
}

export default QuickDuel;
